import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { createStandardMenuItems, Menu } from "@msuite/ui/menu";
import { ConfirmModal, ModalWrapper, useAsyncModal } from "@msuite/ui/modal";
import { useQueryClient } from "@msuite/ui/query";
import { type CellContext, RowActionsWrapper, useDataTableContext } from "@msuite/ui/table";
import type { ContactMember } from "@msuite/utils/types";
import React from "react";
import { client } from "@/hono";
import { useCitadel } from "@/hooks/use-citadel";
import { useHiddenContactTabs } from "@/hooks/use-contact-tabs";
import { EditContactMemberModal } from "@/modals/contacts/edit-contact-member-modal";
import { contactTabPath } from "../tabs";

export function RowActions({ cell }: CellContext<ContactMember, "actions">) {
	/** Context */
	const { queryKey } = useDataTableContext();

	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();
	const editContactMemberModal = useDisclosure();
	const contactId = cell.row.original.contactId;
	const contactMemberId = cell.row.original.id;
	const asyncModal = useAsyncModal();
	const { citadel } = useCitadel();
	const hiddenTabs = useHiddenContactTabs();

	/** Computed */
	// Keeping the people at a client is a right an organisation grants; everybody reads them.
	const canUpdate = citadel?.can("update", "ContactMembers") === true;
	const canDelete = citadel?.can("delete", "ContactMembers") === true;

	/** Functions */
	async function handleOnCompleteEdit() {
		if (!queryKey) return;
		await qc.invalidateQueries({ queryKey });
	}

	async function handleOnDeleteMember() {
		if (!queryKey) return;
		const isConfirmed = await asyncModal.confirm();
		if (!isConfirmed) return;
		await client.contacts[":contactId"].members[":contactMemberId"].$delete({
			param: { contactId: contactId || "", contactMemberId },
		});
		await qc.invalidateQueries({ queryKey });
	}

	/** Render */
	return (
		<React.Fragment>
			<RowActionsWrapper to={contactId ? contactTabPath(contactId, "members", hiddenTabs) : null}>
				<Menu
					items={createStandardMenuItems({
						onEdit: canUpdate ? editContactMemberModal.onOpen : undefined,
						onDelete: canDelete ? handleOnDeleteMember : undefined,
					})}
				>
					<Button
						size="xxs"
						icon={<Icon name="EllipsisVertical" />}
						variant="ghost"
					/>
				</Menu>
			</RowActionsWrapper>
			<ModalWrapper>
				{editContactMemberModal.isOpen && (
					<EditContactMemberModal
						isOpen={editContactMemberModal.isOpen}
						onClose={editContactMemberModal.onClose}
						contactId={contactId}
						contactMemberId={contactMemberId}
						onComplete={handleOnCompleteEdit}
					/>
				)}
				{asyncModal.isOpen && (
					<ConfirmModal
						controller={asyncModal}
						message={t("contacts.delete_contact_member_confirm_message")}
					/>
				)}
			</ModalWrapper>
		</React.Fragment>
	);
}
